import React, {Fragment, useEffect, useRef} from 'react';
import {Controller, useFormContext, useWatch} from 'react-hook-form';

import Input from 'patient-ping-remedy/packages/input';
import {Value} from 'patient-ping-remedy/packages/dropdown-list/lib/DropdownList';
import DropdownList from 'patient-ping-remedy/packages/dropdown-list';
import DatePicker from 'patient-ping-remedy/packages/date-picker';
import {sizes} from 'patient-ping-remedy/packages/theme';
import Spacer from 'patient-ping-remedy/packages/spacer';
import {DatePickerWrapper} from '@common/DatePickerWrapper.js';
import {GenderWrapper, StyledRow} from '@manual_encounters/encounter_client_search/PatientInformationForm.styles';
import DisplayHelpers from '@/helpers/display_helpers';
import {
  genderOptions,
  ethnicityOptions,
  languageOptions,
  maritalStatusOptions,
  raceOptions,
  veteranOptions,
  phoneTypeOptions,
} from '@manual_encounters/constants/ExtendedDemographicsOptions';
import Banner, {BannerTypes} from "patient-ping-remedy/packages/banner";
import helpers from "@/helpers/helpers.js";
import {DemographicFieldRequirements} from "@/api/dto/state-config";

const requiredField = 'Required field';
const invalidValue = 'Invalid value';

const hasAddressValue = (value: unknown) => typeof value === 'string' && !helpers.isEmpty(value.trim());

// A field that stops being required keeps its last error until it is edited again, so ignore it while optional.
const visibleError = <T extends { type?: string }>(error: T | undefined, isRequired: boolean) =>
  !isRequired && error?.type === 'required' ? undefined : error;

type Props = {
  disabled: boolean;
  showAll: boolean;
  isWithinModal: boolean;
  focusOnLoad: boolean;
  featureName: string;
  modalType: string;
  isReferralModal?: boolean;
  demographicFieldRequirements?: DemographicFieldRequirements;
}

export default function PatientDemographics({
  disabled = false,
  showAll = true,
  isWithinModal = true,
  focusOnLoad = true,
  featureName = 'PatientAdmit',
  modalType = 'patient-admit',
  isReferralModal = false,
  demographicFieldRequirements,
}: Props) {
  const firstNameRef = useRef<HTMLInputElement | null>(null);
  const { register, control, errors, formState, setValue } = useFormContext();
  const { dirtyFields } = formState;

  useEffect(() => {
    if (focusOnLoad) {
      firstNameRef.current?.focus();
    }
  }, []);

  const { clientId, dob, ssn, zip, phone, phoneType, address1, address2, city, state, ethnicity, veteranStatus, language, race,
    maritalStatus } =
    useWatch({ name: ['clientId', 'dob', 'ssn', 'zip', 'phone', 'phoneType', 'address1', 'address2', 'city', 'state',
      'ethnicity', 'veteranStatus', 'language', 'race', 'maritalStatus'] });

  const age = dob ? helpers.getAge(dob) : null;
  const isMinor = typeof age === 'number' && age < 18;
  const hasPhone = typeof phone === 'string' && /\d/.test(phone);
  // Entering any part of an address in the admit modal requires the rest of it, except Address 2.
  const requiresCompleteAddress = !isReferralModal && [address1, address2, city, state, zip].some(hasAddressValue);
  const isAddress1Required = requiresCompleteAddress || !!demographicFieldRequirements?.address1;
  const isCityRequired = requiresCompleteAddress || !!demographicFieldRequirements?.city;
  const isStateRequired = requiresCompleteAddress || !!demographicFieldRequirements?.state;
  const isZipRequired = requiresCompleteAddress || !!demographicFieldRequirements?.zip;
  const address1Error = visibleError(errors.address1, isAddress1Required);
  const cityError = visibleError(errors.city, isCityRequired);
  const stateError = visibleError(errors.state, isStateRequired);
  const zipError = visibleError(errors.zip, isZipRequired);

  useEffect(() => {
    if (!hasPhone) {
      setValue('phoneType', null);
    }
  }, [hasPhone, setValue]);

  const isSSNDisabled = (disabled && ssn && !dirtyFields.ssn && !modalType);
  const isClientIdDisabled = (disabled && clientId && !dirtyFields.clientId && !modalType);
  const isZipDisabled = (disabled && zip && !dirtyFields.zip && !modalType);
  const isPhoneDisabled = (disabled && phone && !dirtyFields.phone && !modalType);
  const isPhoneTypeDisabled = (disabled && phoneType && !dirtyFields.phoneType && !modalType);
  const isAddress1Disabled = (disabled && address1 && !dirtyFields.address1 && !modalType);
  const isAddress2Disabled = (disabled && address2 && !dirtyFields.address2 && !modalType);
  const isStateDisabled = (disabled && state && !dirtyFields.state && !modalType);
  const isCityDisabled = (disabled && city && !dirtyFields.city && !modalType);
  const isEthnicityDisabled = (disabled && ethnicity && !dirtyFields.ethnicity && !modalType);
  const isVeteranStatusDisabled = (disabled && veteranStatus && !dirtyFields.veteranStatus && !modalType);
  const isLanguageDisabled = (disabled && language && !dirtyFields.language && !modalType);
  const isRaceDisabled = (disabled && race && !dirtyFields.race && !modalType);
  const isMaritalStatusDisabled = (disabled && maritalStatus && !dirtyFields.maritalStatus && !modalType);

  return (
    <Fragment>
      <StyledRow>
        <Input
          name="firstName"
          labelText="First Name"
          id="firstName"
          error={errors?.firstName}
          errorMessage={requiredField}
          required="required"
          isDisabled={disabled}
          ref={(input) => {
            register(input, {
              required: showAll ? requiredField : false,
              maxLength: 50,
              minLength: showAll ? 2 : 0,
              pattern: /^[A-Za-z]/,
            });
            firstNameRef.current = input;
          }}
        />
        <Spacer itemWidth={sizes.large} />
        <Input
          name="lastName"
          labelText="Last Name"
          id="lastName"
          error={errors?.lastName}
          errorMessage={requiredField}
          required="required"
          isDisabled={disabled}
          ref={register({
            required: showAll ? requiredField : false,
            maxLength: 50,
            minLength: showAll ? 2 : 0,
            pattern: /^[A-Za-z]/,
          })}
        />
      </StyledRow>
      <StyledRow>
        <Input
          name="clientId"
          id="clientId"
          type="text"
          labelText="Client ID"
          required="optional"
          autoFocus={focusOnLoad}
          isDisabled={isClientIdDisabled}
          placeholder="Enter id"
          ref={register({ required: false })}
        />
      </StyledRow>

      {showAll && (
        <Fragment>
          <StyledRow>
            <GenderWrapper>
              <Controller
                name="gender"
                control={control}
                rules={{required: true}}
                render={({onChange, value, onBlur}) => (
                  <DropdownList
                    handleChange={(newValue: Value | null) => onChange(newValue?.value)}
                    onBlur={onBlur}
                    value={genderOptions.find((g) => g.value === value)}
                    id="gender"
                    items={genderOptions}
                    label="Gender"
                    disabled={disabled}
                    isWithinModal={isWithinModal}
                    required
                    error={errors?.gender}
                    errorMessage={requiredField}
                  />
                )}
              />
            </GenderWrapper>
            <Controller
              name="dob"
              control={control}
              rules={{required: true}}
              render={({onChange, onBlur, value}) => (
                <DatePickerWrapper>
                  <DatePicker
                    value={value ? new Date(value) : null}
                    onChange={(date) => {
                      onChange(date);
                      return date;
                    }}
                    disabled={disabled}
                    maxDate={new Date()}
                    externalError={errors?.dob}
                    externalErrorText={requiredField}
                    id={`dob${featureName}`}
                    onBlur={onBlur}
                    labelText="Date of Birth"
                    required="required"
                  />
                </DatePickerWrapper>
              )}
            />
          </StyledRow>
          <StyledRow>
            {/* UE-3050 Only validate if field is enabled */}
            <Input
              name="ssn"
              id="ssn"
              type="text"
              labelText="SSN - Last Four"
              required={demographicFieldRequirements?.ssn ? "required" : "optional"}
              isDisabled={isSSNDisabled}
              error={errors?.ssn}
              errorMessage={errors?.ssn?.type === 'required' ? requiredField : (errors.ssn && invalidValue)}
              placeholder="####"
              maxLength={4}
              ref={register(
                isSSNDisabled
                  ? {required: false}
                  : {
                    required: demographicFieldRequirements?.ssn ? requiredField : false,
                    pattern: /^(?!0000)[0-9]{4}$/,
                    minLength: 4,
                    maxLength: 4,
                  },
              )}
            />
            {isMinor && (
              <Banner type={BannerTypes.WARNING}>
                Client is under the age of 18
              </Banner>
            )}
          </StyledRow>
          <Input
            name="address1"
            id="address1"
            type="text"
            labelText="Address 1"
            required={isAddress1Required ? "required" : "optional"}
            isDisabled={isAddress1Disabled}
            error={address1Error}
            errorMessage={address1Error?.type === 'required' ? requiredField : (address1Error && invalidValue)}
            maxLength={100}
            ref={register({
              required: isAddress1Required ? requiredField : false,
              maxLength: 100,
            })}
          />
          <Spacer itemWidth={sizes.large}/>
          <Input
            name="address2"
            id="address2"
            type="text"
            labelText="Address 2"
            required={demographicFieldRequirements?.address2 ? "required" : "optional"}
            isDisabled={isAddress2Disabled}
            error={errors.address2}
            errorMessage={errors?.address2?.type === 'required' ? requiredField : (errors.address2 && invalidValue)}
            maxLength={100}
            ref={register({
              required: demographicFieldRequirements?.address2 ? requiredField : false,
              maxLength: 100,
            })}
          />
          <StyledRow>
            <Input
              name="city"
              id="city"
              type="text"
              labelText="City"
              required={isCityRequired ? "required" : "optional"}
              isDisabled={isCityDisabled}
              error={cityError}
              errorMessage={cityError?.type === 'required' ? requiredField : (cityError && invalidValue)}
              maxLength={50}
              ref={register({
                required: isCityRequired ? requiredField : false,
                maxLength: 50,
              })}
            />
            <Input
              name="state"
              id="state"
              type="text"
              labelText="State"
              required={isStateRequired ? "required" : "optional"}
              isDisabled={isStateDisabled}
              error={stateError}
              errorMessage={stateError?.type === 'required' ? requiredField : (stateError && invalidValue)}
              maxLength={2}
              ref={register({
                required: isStateRequired ? requiredField : false,
                pattern: /^[A-Za-z]{2}$/,
                minLength: 2,
                maxLength: 2,
              })}
            />
          </StyledRow>
          <StyledRow>
            <Input
              name="zip"
              id="zip"
              type="text"
              labelText="Zip Code"
              required={isZipRequired ? "required" : "optional"}
              isDisabled={isZipDisabled}
              error={zipError}
              errorMessage={zipError?.type === 'required' ? requiredField : (zipError && invalidValue)}
              maxLength={5}
              ref={register({
                required: isZipRequired ? requiredField : false,
                pattern: /^[0-9]{5}$/,
                minLength: 5,
              })}
            />
            <Controller
              name="language"
              control={control}
              rules={{required: demographicFieldRequirements?.language ? requiredField : false}}
              defaultValue={null}
              render={({onChange, value, onBlur}) => (
                <DropdownList
                  handleChange={(newValue: Value | null) => onChange(newValue?.value)}
                  onBlur={onBlur}
                  isClearable={!demographicFieldRequirements?.language}
                  disabled={isLanguageDisabled}
                  value={languageOptions.find((language) => language.value === value)}
                  id="language"
                  items={languageOptions}
                  label="Language"
                  labelMessage={demographicFieldRequirements?.language ? undefined : "optional"}
                  required={!!demographicFieldRequirements?.language}
                  error={errors?.language}
                  errorMessage={requiredField}
                />
              )}
            />
          </StyledRow>
          <StyledRow>
            <Controller
              name="phone"
              control={control}
              rules={{
                required: demographicFieldRequirements?.phone ? requiredField : false,
                pattern: {
                  value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im,
                  message: 'Phone number must be in the format (XXX) XXX-XXXX.',
                },
              }}
              defaultValue={null}
              render={({onChange, value, onBlur}) => (
                <Input
                  name="phone"
                  id="phone"
                  type="text"
                  labelText="Phone Number"
                  required={demographicFieldRequirements?.phone ? "required" : "optional"}
                  value={DisplayHelpers.formatPhoneNumber(value)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const raw = e.target.value.replace(/\D/g, '').substring(0, 10);
                    onChange(raw);
                  }}
                  onBlur={onBlur}
                  placeholder="(___) ___-____"
                  isDisabled={isPhoneDisabled}
                  error={errors?.phone}
                  errorMessage={
                    errors?.phone?.type === 'required'
                      ? requiredField
                      : (errors?.phone && errors?.phone.message)
                  }
                />
              )}
            />
            {hasPhone && (
              <>
                <Spacer itemWidth={sizes.large}/>
                <Controller
                  name="phoneType"
                  control={control}
                  rules={{required: hasPhone ? requiredField : false}}
                  defaultValue={null}
                  render={({onChange, value, onBlur}) => (
                    <DropdownList
                      handleChange={(newValue: Value | null) => onChange(newValue?.value)}
                      onBlur={onBlur}
                      isClearable={false}
                      disabled={isPhoneTypeDisabled}
                      value={phoneTypeOptions.find((phoneType) => phoneType.value === value)}
                      id="phoneType"
                      items={phoneTypeOptions}
                      label="Phone Type"
                      labelMessage={undefined}
                      required={true}
                      error={errors?.phoneType}
                      errorMessage={requiredField}
                    />
                  )}
                />
              </>
            )}
          </StyledRow>
          <StyledRow>
            <Controller
              name="maritalStatus"
              control={control}
              rules={{required: demographicFieldRequirements?.maritalStatus ? requiredField : false}}
              defaultValue={null}
              render={({onChange, value, onBlur}) => (
                <DropdownList
                  handleChange={(newValue: Value | null) => onChange(newValue?.value)}
                  onBlur={onBlur}
                  isClearable={!demographicFieldRequirements?.maritalStatus}
                  disabled={isMaritalStatusDisabled}
                  value={maritalStatusOptions.find((maritalStatus) => maritalStatus.value === value)}
                  id="maritalStatus"
                  items={maritalStatusOptions}
                  label="Marital Status"
                  labelMessage={demographicFieldRequirements?.maritalStatus ? undefined : "optional"}
                  required={!!demographicFieldRequirements?.maritalStatus}
                  error={errors?.maritalStatus}
                  errorMessage={requiredField}
                />
              )}
            />
            <Controller
              name="veteranStatus"
              control={control}
              rules={{required: demographicFieldRequirements?.veteranStatus ? requiredField : false}}
              defaultValue={null}
              render={({onChange, value, onBlur}) => (
                <DropdownList
                  handleChange={(newValue: Value | null) => onChange(newValue?.value)}
                  onBlur={onBlur}
                  isClearable={!demographicFieldRequirements?.veteranStatus}
                  disabled={isVeteranStatusDisabled}
                  value={veteranOptions.find((veteranStatus) => veteranStatus.value === value)}
                  id="veteranStatus"
                  items={veteranOptions}
                  label="Veteran Status"
                  labelMessage={demographicFieldRequirements?.veteranStatus ? undefined : "optional"}
                  required={!!demographicFieldRequirements?.veteranStatus}
                  error={errors?.veteranStatus}
                  errorMessage={requiredField}
                />
              )}
            />
          </StyledRow>
          <StyledRow>
            <Controller
              name="race"
              control={control}
              rules={{required: demographicFieldRequirements?.race ? requiredField : false}}
              defaultValue={null}
              render={({onChange, value, onBlur}) => (
                <DropdownList
                  handleChange={(newValue: Value | null) => onChange(newValue?.value)}
                  onBlur={onBlur}
                  isClearable={!demographicFieldRequirements?.race}
                  disabled={isRaceDisabled}
                  value={raceOptions.find((race) => race.value === value)}
                  id="race"
                  items={raceOptions}
                  label="Race"
                  labelMessage={demographicFieldRequirements?.race ? undefined : "optional"}
                  required={!!demographicFieldRequirements?.race}
                  error={errors?.race}
                  errorMessage={requiredField}
                />
              )}
            />
            <Controller
              name="ethnicity"
              control={control}
              rules={{required: demographicFieldRequirements?.ethnicity ? requiredField : false}}
              defaultValue={null}
              render={({onChange, value, onBlur}) => (
                <DropdownList
                  handleChange={(newValue: Value | null) => onChange(newValue?.value)}
                  onBlur={onBlur}
                  isClearable={!demographicFieldRequirements?.ethnicity}
                  disabled={isEthnicityDisabled}
                  value={ethnicityOptions.find((ethnicity) => ethnicity.value === value)}
                  id="ethnicity"
                  items={ethnicityOptions}
                  label="Ethnicity"
                  labelMessage={demographicFieldRequirements?.ethnicity ? undefined : "optional"}
                  required={!!demographicFieldRequirements?.ethnicity}
                  error={errors?.ethnicity}
                  errorMessage={requiredField}
                />
              )}
            />
          </StyledRow>
        </Fragment>
      )}
    </Fragment>
  );
}
